import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Download, FileText, Loader2, AlertCircle } from 'lucide-react';

interface LandingInfo {
  id: string;
  vanity_slug: string;
  file_name: string | null;
  file_path: string | null;
  og_image_path: string | null;
  landing_title: string | null;
  landing_description: string | null;
  view_count: number;
}

export default function LandingPage() {
  const { slug, action } = useParams<{ slug: string; action?: string }>();
  const [landing, setLanding] = useState<LandingInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

  useEffect(() => {
    if (!slug) {
      setError('Brak identyfikatora strony');
      setLoading(false);
      return;
    }

    const fetchLanding = async () => {
      try {
        const { data, error: fetchError } = await supabase
          .from('file_uploads')
          .select('id, vanity_slug, file_name, file_path, og_image_path, landing_title, landing_description, view_count')
          .eq('vanity_slug', slug)
          .single();

        if (fetchError || !data) {
          setError('Strona nie została znaleziona');
          return;
        }

        setLanding(data);

        // If this is a /download action, trigger download immediately
        if (action === 'download') {
          handleDownload(data);
        }

        // Fire pixel for self-heal (non-blocking)
        fetch(`${supabaseUrl}/functions/v1/landing/${slug}/pixel`).catch(() => {});
      } catch (err) {
        setError('Wystąpił błąd podczas ładowania strony');
      } finally {
        setLoading(false);
      }
    };

    fetchLanding();
  }, [slug, action, supabaseUrl]);

  const handleDownload = async (data?: LandingInfo) => {
    const landingData = data || landing;
    if (!landingData?.file_path) return;

    setDownloading(true);

    try {
      // Call edge function to increment view_count and get redirect
      const response = await fetch(`${supabaseUrl}/functions/v1/landing/${slug}/download`, {
        method: 'GET',
        redirect: 'follow',
      });

      if (response.ok || response.redirected) {
        // Get the final URL (after redirect) or construct it
        const pdfUrl = response.url || getPdfUrl(landingData);
        
        // Trigger download
        const link = document.createElement('a');
        link.href = pdfUrl;
        link.download = landingData.file_name || 'download.pdf';
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (err) {
      // Fallback: direct download
      const pdfUrl = getPdfUrl(landingData);
      window.open(pdfUrl, '_blank');
    } finally {
      setDownloading(false);
    }
  };

  const getPdfUrl = (data: LandingInfo): string => {
    if (!data.file_path) return '';
    const cleanPath = data.file_path.replace(/^file-uploads\//, '');
    return `${supabaseUrl}/storage/v1/object/public/file-uploads/${cleanPath}`;
  };

  const getOgImageUrl = (): string | null => {
    if (!landing?.og_image_path) return null;
    const cleanPath = landing.og_image_path.replace(/^file-uploads\//, '');
    return `${supabaseUrl}/storage/v1/object/public/file-uploads/${cleanPath}`;
  };

  const getCurrentUrl = (): string => {
    return `${window.location.origin}/l/${slug}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="flex items-center gap-3 text-white">
          <Loader2 className="h-8 w-8 animate-spin" />
          <span>Ładowanie...</span>
        </div>
      </div>
    );
  }

  if (error || !landing) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-slate-800/90 border-slate-700 backdrop-blur-lg">
          <CardContent className="pt-8 pb-8 text-center">
            <AlertCircle className="h-16 w-16 text-red-400 mx-auto mb-4" />
            <h1 className="text-2xl font-semibold text-white mb-2">Nie znaleziono strony</h1>
            <p className="text-slate-400 mb-6">{error || 'Strona nie istnieje lub link wygasł'}</p>
            <Button asChild variant="outline" className="border-slate-600 text-slate-200 hover:bg-slate-700">
              <Link to="/">Wróć na stronę główną</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const ogImageUrl = getOgImageUrl();
  const pageTitle = landing.landing_title || landing.file_name || 'Pobierz materiał';
  const pageDescription = landing.landing_description || `Pobierz: ${landing.file_name || 'materiał'}`;

  return (
    <>
      <Helmet>
        <title>{pageTitle} | LIFactor</title>
        <meta name="description" content={pageDescription} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={getCurrentUrl()} />
        {ogImageUrl && <meta property="og:image" content={ogImageUrl} />}
        {ogImageUrl && <meta property="og:image:width" content="1200" />}
        {ogImageUrl && <meta property="og:image:height" content="630" />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        {ogImageUrl && <meta name="twitter:image" content={ogImageUrl} />}
        <link rel="canonical" href={getCurrentUrl()} />
      </Helmet>
      
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <Card className="max-w-lg w-full bg-slate-800/90 border-slate-700 backdrop-blur-lg shadow-2xl">
          <CardContent className="pt-10 pb-10 text-center">
            {ogImageUrl ? (
              <div className="mb-7">
                <img 
                  src={ogImageUrl} 
                  alt={pageTitle}
                  className="max-h-52 max-w-full mx-auto object-contain rounded-xl border border-slate-600"
                />
              </div>
            ) : (
              <div className="w-20 h-20 mx-auto mb-7 bg-gradient-to-br from-blue-500 to-purple-500 rounded-2xl flex items-center justify-center">
                <FileText className="h-10 w-10 text-white" />
              </div>
            )}
            
            <h1 className="text-2xl font-bold text-white mb-3 break-words leading-tight">
              {pageTitle}
            </h1>
            
            {pageDescription && (
              <p className="text-slate-300 text-base mb-7 leading-relaxed">
                {pageDescription}
              </p>
            )}
            
            <Button 
              onClick={() => handleDownload()}
              disabled={downloading || !landing.file_path}
              size="lg"
              className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white shadow-lg shadow-blue-500/30 text-lg py-6"
            >
              {downloading ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Pobieranie...
                </>
              ) : (
                <>
                  <Download className="mr-2 h-5 w-5" />
                  Pobierz teraz
                </>
              )}
            </Button>
            
            <p className="mt-8 text-xs text-slate-500">
              Materiał udostępniony przez{' '}
              <a href="https://lifactor.com" target="_blank" rel="noopener" className="text-blue-400 hover:underline">
                LIFactor
              </a>
            </p>
          </CardContent>
        </Card>
      </div>
    </>
  );
}
